<template>
  <section class="page">
    <div class="eyebrow">🩺 临床问诊 · 标准化病人</div>
    <div class="heading">
      <div>
        <h1>标准化病人 · <em>主诉驱动诊断 · 多轮问诊训练</em></h1>
        <p>输入主诉，AI 模拟真实病人与你多轮对话，采集关键信息后自动生成鉴别诊断推理链</p>
      </div>
    </div>

    <!-- 未开会话：输入主诉 -->
    <template v-if="!started">
      <div class="composer">
        <input v-model="complaint" class="ai-input" placeholder="输入主诉，如：胸痛 30 分钟、发热 3 天…" :disabled="starting" @keyup.enter="start">
        <button class="btn" :disabled="starting || !complaint.trim()" @click="start">{{ starting ? '创建中…' : '🩺 开始问诊' }}</button>
      </div>
      <example-chips :examples="examples" @select="complaint = $event" />
      <gate-banner v-if="startError" :error="startError" />
      <empty-state icon="🩺" title="标准化病人问诊训练" description="输入主诉后，AI 将扮演真实病人与你多轮对话。请按 OPQRST/SAMPLE 框架问诊，信息采集完成后可生成鉴别诊断推理链…" />
    </template>

    <!-- 会话中 -->
    <div v-else class="patient-grid">
      <div class="chat-panel">
        <div class="chat-toolbar">
          <b>👤 病人 · {{ title }}</b>
          <span v-if="demo" class="badge badge-mock">⚠ 演示数据</span>
          <span class="spacer" />
          <button class="btn btn-ghost btn-sm" @click="restart">结束并重开</button>
        </div>
        <div v-if="done" class="ai-output-warning">病人已结束对话。信息采集完成？点击右侧生成鉴别诊断推理链。</div>
        <chat-thread :messages="messages" :typing="typing" avatar="🧓" />
        <chat-input
          ref="input"
          placeholder="向病人提问，如：您哪里不舒服？疼了多久了？"
          :disabled="typing"
          @send="onSend"
        />
      </div>
      <aside class="dx-panel">
        <div class="section-title">🔎 采集完成后的推理链</div>
        <p class="hint">基于本段问诊对话生成鉴别诊断（雷达评分 + 分支树 + 建议检查）</p>
        <button class="btn" style="width:100%" :disabled="dxLoading || !messages.length" @click="generateDx">
          {{ dxLoading ? '推理中…' : '🔬 生成鉴别诊断' }}
        </button>
        <div v-if="dxLoading" style="margin-top:14px">
          <div class="skeleton" /><div class="skeleton" style="width:85%" /><div class="skeleton" style="width:70%" />
        </div>
        <template v-else-if="dx">
          <div class="section-title" style="margin-top:16px">诊断分支</div>
          <branch-tree :branches="dx.branches || []" />
        </template>
      </aside>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import { callAI } from '../api';
import EmptyState from '../components/EmptyState.vue';
import GateBanner from '../components/GateBanner.vue';
import ExampleChips from '../components/ExampleChips.vue';
import ChatThread from '../components/ChatThread.vue';
import ChatInput from '../components/ChatInput.vue';
import BranchTree from '../components/BranchTree.vue';

export default {
  name: 'PatientSim',
  components: { EmptyState, GateBanner, ExampleChips, ChatThread, ChatInput, BranchTree },
  data: () => ({
    complaint: '',
    examples: ['胸痛 30 分钟', '腹痛 6 小时', '发热 3 天', '咯血 1 天'],
    starting: false,
    startError: null,
    done: false,
    dx: null,
    dxLoading: false,
  }),
  computed: {
    ...mapState('chat', ['sessionId', 'title', 'messages', 'typing', 'demo']),
    started() { return !!this.sessionId; },
  },
  methods: {
    async start() {
      if (!this.complaint.trim() || this.starting) return;
      this.starting = true;
      try {
        await this.$store.dispatch('chat/start', { module: 'patient', title: this.complaint.trim() });
        this.done = false;
        this.dx = null;
        this.$refs.input && this.$nextTick(() => this.$refs.input.$el.querySelector('input').focus());
      } catch (e) {
        this.startError = { code: e.code, msg: `创建会话失败：${e.msg || e.message}` };
      } finally {
        this.starting = false;
      }
    },
    async onSend(text) {
      try {
        const res = await this.$store.dispatch('chat/send', text);
        if (res && res.done) this.done = true;
        this.startError = null;
      } catch (e) {
        this.startError = { code: e.code, msg: e.msg || e.message };
      }
    },
    async generateDx() {
      if (this.dxLoading || !this.messages.length) return;
      this.dxLoading = true;
      try {
        const dialog = this.messages.map((m) => `${m.role === 'user' ? '医学生' : '病人'}：${m.content}`).join('\n');
        const res = await callAI('dx-chain', { complaint: this.title, history: dialog });
        this.dx = res.parsed;
      } finally {
        this.dxLoading = false;
      }
    },
    restart() {
      this.$store.commit('chat/reset', 'patient');
      this.done = false;
      this.dx = null;
    },
  },
};
</script>

<style scoped>
.patient-grid { display: grid; grid-template-columns: minmax(0, 1.7fr) minmax(0, 1fr); gap: 18px; margin-top: 18px; }
.chat-panel { background: var(--bg-2); border: 1px solid var(--border-2); border-radius: var(--radius-l); padding: 16px; }
.chat-toolbar { display: flex; align-items: center; gap: 10px; font-size: 13px; margin-bottom: 12px; }
.chat-toolbar .spacer { flex: 1; }
.btn-sm { padding: 4px 12px; font-size: 12px; }
.dx-panel { background: transparent; border: 0; min-width: 0; }
.dx-panel .section-title { overflow-wrap: anywhere; }
@media (max-width: 900px) { .patient-grid { grid-template-columns: 1fr; } }
</style>
